import { type ReactNode, createContext, useContext, useState, useCallback, useMemo } from 'react'
import { useCustom } from '@refinedev/core'
import type { TPublisherSong } from '@modules/types'

interface ShareModalContextType {
  open: boolean
  selected: number[]
  songs: TPublisherSong[]
  requestId?: number
  requestTitle?: string
  distributionTerritory?: string
  plannedPublicationDate?: string
  isLoading: boolean
  sharedUrl?: string
  sharedPassword?: string
  handleOpen: (
    songs: TPublisherSong[],
    requestId?: number,
    requestTitle?: string,
    distributionTerritory?: string,
    plannedPublicationDate?: string,
  ) => void
  handleClose: () => void
  handleSelect: (id: number) => void
  handleSelectAll: (checked: boolean) => void
  handleShare: () => Promise<void>
}

const ShareModalContext = createContext<ShareModalContextType | undefined>(undefined)

export const useShareModalContext = () => {
  const context = useContext(ShareModalContext)
  if (!context) {
    throw new Error('useShareModalContext must be used within a ShareModalProvider')
  }
  return context
}

interface ShareModalProviderProps {
  children: ReactNode
}

export const ShareModalProvider = ({ children }: ShareModalProviderProps) => {
  const [open, setOpen] = useState(false)
  const [selected, setSelected] = useState<number[]>([])
  const [songs, setSongs] = useState<TPublisherSong[]>([])
  const [requestId, setRequestId] = useState<number>()
  const [requestTitle, setRequestTitle] = useState<string>()
  const [distributionTerritory, setDistributionTerritory] = useState<string>()
  const [plannedPublicationDate, setPublicationDate] = useState<string>()
  const [isLoading, setIsLoading] = useState(false)
  const [sharedUrl, setSharedUrl] = useState<string>()
  const [sharedPassword, setSharedPassword] = useState<string>()

  const { refetch: shareSongs } = useCustom({
    url: requestId ? `/publisher-request/${requestId}/share` : '',
    method: 'post',
    config: {
      payload: {
        // eslint-disable-next-line camelcase
        song_ids: selected,
      },
    },
    queryOptions: {
      enabled: false,
    },
  })

  const handleOpen = useCallback(
    (
      newSongs: TPublisherSong[],
      newRequestId?: number,
      newRequestTitle?: string,
      newDistributionTerritory?: string,
      newPublicationDate?: string,
    ) => {
      setSongs(newSongs)
      setSelected(newSongs.map(s => s.id))
      setRequestId(newRequestId)
      setRequestTitle(newRequestTitle)
      setDistributionTerritory(newDistributionTerritory)
      setPublicationDate(newPublicationDate)
      setSharedUrl(undefined)
      setSharedPassword(undefined)
      setOpen(true)
    },
    [],
  )

  const handleClose = useCallback(() => {
    setOpen(false)
    setSelected([])
    setSongs([])
    setRequestId(undefined)
    setRequestTitle(undefined)
    setDistributionTerritory(undefined)
    setPublicationDate(undefined)
    setIsLoading(false)
    setSharedUrl(undefined)
    setSharedPassword(undefined)
  }, [])

  const handleSelect = useCallback((id: number) => {
    setSelected(prev => (prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]))
  }, [])

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelected(checked ? songs.map(s => s.id) : [])
    },
    [songs],
  )

  const handleShare = useCallback(async () => {
    if (!requestId || selected.length === 0) return

    setIsLoading(true)
    try {
      const result = await shareSongs()
      const responseData = result.data?.data
      setSharedUrl(responseData?.url)
      setSharedPassword(responseData?.plainPassword)
    } catch (error) {
      // Error sharing songs
    } finally {
      setIsLoading(false)
    }
  }, [requestId, selected, shareSongs])

  const value: ShareModalContextType = useMemo(
    () => ({
      open,
      selected,
      songs,
      requestId,
      requestTitle,
      distributionTerritory,
      plannedPublicationDate,
      isLoading,
      sharedUrl,
      sharedPassword,
      handleOpen,
      handleClose,
      handleSelect,
      handleSelectAll,
      handleShare,
    }),
    [
      open,
      selected,
      songs,
      requestId,
      requestTitle,
      distributionTerritory,
      plannedPublicationDate,
      isLoading,
      sharedUrl,
      sharedPassword,
      handleOpen,
      handleClose,
      handleSelect,
      handleSelectAll,
      handleShare,
    ],
  )

  return <ShareModalContext.Provider value={value}>{children}</ShareModalContext.Provider>
}
